import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Quote, ClipboardPaste, Bot, Save, Check, Copy, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { extractVideoId, streamSummary } from "./utils";
import { autoSaveSummary } from "./autoSave";
import ReactMarkdown from "react-markdown";

const MODEL_OPTIONS = [
  { value: "auto", label: "🤖 Auto" },
  { value: "gemini-2.5-pro", label: "⚡ Gemini 2.5 Pro" },
  { value: "gpt-5", label: "🎯 GPT-5" },
  { value: "gpt-5.2", label: "🧠 GPT-5.2" },
  { value: "gemini-3.1-pro", label: "🏆 Gemini 3.1 Pro" },
];

interface Props {
  userId?: string;
}

export function KeyQuotesMode({ userId }: Props) {
  const [url, setUrl] = useState("");
  const [model, setModel] = useState("auto");
  const [quotesText, setQuotesText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [meta, setMeta] = useState<{ author?: string; model?: string; language?: string }>({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const { toast } = useToast();

  const videoId = extractVideoId(url);

  const isValidYoutubeUrl = (u: string) =>
    /(?:youtube\.com\/(?:watch|embed|shorts)|youtu\.be\/)/.test(u);

  const handleStart = async () => {
    const trimmed = url.trim();
    if (!trimmed || !isValidYoutubeUrl(trimmed)) {
      toast({ title: "Invalid URL", description: "Please enter a valid YouTube URL.", variant: "destructive" });
      return;
    }
    setQuotesText("");
    setStatus("loading");
    setIsProcessing(true);
    setSaved(false);
    setMeta({});
    abortRef.current = new AbortController();

    try {
      const result = await streamSummary(
        trimmed,
        (text) => setQuotesText(text),
        abortRef.current.signal,
        (m) => setMeta(m),
        model,
        "quotes"
      );
      setQuotesText(result.summary);
      setMeta({ author: result.author, model: result.model, language: result.language });
      setStatus("done");
      // Auto-save
      const vid = extractVideoId(trimmed);
      const didSave = await autoSaveSummary({
        userId,
        videoUrl: trimmed,
        videoId: vid,
        summaryContent: `[KEY QUOTES]\n\n${result.summary}`,
        author: result.author,
      });
      if (didSave) setSaved(true);
    } catch (err: any) {
      if (err.name !== "AbortError") {
        setStatus("error");
        toast({ title: "Error", description: err.message, variant: "destructive" });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSave = async () => {
    if (!userId) {
      toast({ title: "Sign in required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase.from("content_summaries").insert({
      user_id: userId,
      video_url: url,
      video_id: videoId,
      summary_content: `[KEY QUOTES]\n\n${quotesText}`,
      author: meta.author || null,
    });
    setSaving(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } else {
      setSaved(true);
      toast({ title: "Saved", description: "Key quotes saved to Collected Data." });
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(quotesText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const quotesCount = (quotesText.match(/> "/g) || quotesText.match(/^>/gm) || []).length;
  const progress = status === "loading"
    ? Math.min(90, Math.max(10, quotesCount * 8 + 10))
    : status === "done" ? 100 : 0;

  return (
    <div className="space-y-6">
      <Card className="border-amber-500/10 bg-card/90 backdrop-blur-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Quote className="h-5 w-5 text-amber-500" />
            <CardTitle className="text-lg">Key Quotes Extractor</CardTitle>
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardDescription>
              Extract notable quotes with YouTube timestamps from any video.
            </CardDescription>
            <div className="flex items-center gap-2">
              <Bot className="h-4 w-4 text-muted-foreground" />
              <Select value={model} onValueChange={setModel} disabled={isProcessing}>
                <SelectTrigger className="h-8 w-[160px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODEL_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="https://www.youtube.com/watch?v=..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={isProcessing}
              className="flex-1"
            />
            <Button
              variant="outline"
              size="icon"
              disabled={isProcessing}
              title="Paste"
              onClick={async () => {
                try {
                  const text = await navigator.clipboard.readText();
                  if (text.trim()) setUrl(text.trim());
                } catch {}
              }}
            >
              <ClipboardPaste className="h-4 w-4" />
            </Button>
            <Button
              onClick={handleStart}
              disabled={isProcessing || !url.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Quote className="h-4 w-4 mr-2" />}
              {isProcessing ? "Extracting…" : "Extract Quotes"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {status !== "idle" && (
        <Card className="border-amber-500/10">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-base flex items-center gap-2">
                <Quote className="h-4 w-4 text-amber-500" />
                Key Quotes
              </CardTitle>
              {meta.model && (
                <Badge variant="outline" className="text-[10px] gap-1 font-normal">
                  <Bot className="h-3 w-3" /> {meta.model}
                </Badge>
              )}
              {quotesCount > 0 && (
                <Badge variant="secondary" className="text-[10px]">
                  {quotesCount} quotes found
                </Badge>
              )}
            </div>
            {status === "done" && (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={handleSave} disabled={saving || saved}>
                  {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : saved ? <Check className="h-4 w-4 mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  {saving ? "Saving…" : saved ? "Saved" : "Save"}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleCopy}>
                  {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {status === "loading" && (
              <div className="space-y-2 mb-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin text-amber-500" />
                    Extracting quotes… ({quotesCount} found so far)
                  </span>
                  <span className="font-medium">{progress}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            )}
            {quotesText && (
              <ScrollArea className="max-h-[600px]">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown
                    components={{
                      a: ({ href, children }) => (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                          {children} <ExternalLink className="h-3 w-3 inline" />
                        </a>
                      ),
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-amber-500/50 bg-amber-500/5 pl-4 py-2 my-3 italic">
                          {children}
                        </blockquote>
                      ),
                    }}
                  >
                    {quotesText}
                  </ReactMarkdown>
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
